// 約定結果タブのフィルタを約定結果 CSV の RPC (AC-BSP-115) の条件に、 応答の行を CSV の行に写す。
// 出す行・未着件数・約定電力量・約定金額は backend が決める (ここでは計算しない)。
import { create } from '@bufbuild/protobuf'
import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import {
  BiddingExecutionResultBidKind,
  type BiddingExecutionResultLine,
  BiddingExecutionResultStatus,
  BiddingExecutionResultStatusSelectionSchema,
  BiddingExecutionResultUnarrivedStatus,
  BiddingExecutionResultWorkflowSelectionSchema,
} from '@ebalance/proto/bffuser/v1/bidding_execution_result_model_pb'
import {
  type ListBiddingSpotExecutionResultsRequest,
  ListBiddingSpotExecutionResultsRequestSchema,
} from '@ebalance/proto/bffuser/v1/bidding_spot_service_pb'

import { civilDateToProtoDate, protoDateToCivilDate } from '~/models/civilDate'
import type { DeliveryDatePeriod } from '~/models/deliveryDatePeriod'
import type {
  ExecutionResultBidKind,
  ExecutionResultCsvLine,
  ExecutionResultStatus,
} from '~/models/executionResultCsv'
import { NO_WORKFLOW } from '~/models/workflowFilter'
import type { SpotBidStatusCategory } from '~/pages/SpotBidding/models/spotBidStatusCategory'
import type { SpotExecutionResultFilter } from '~/pages/SpotBidding/models/spotExecutionResult'

const SETTLED_STATUS: Partial<
  Record<SpotBidStatusCategory, BiddingExecutionResultStatus>
> = {
  allMatched: BiddingExecutionResultStatus.ALL_MATCHED,
  partialMatched: BiddingExecutionResultStatus.PARTIALLY_MATCHED,
  unmatched: BiddingExecutionResultStatus.UNMATCHED,
}

// 約定結果が未着の札 (AC-BSP-101) がステータス列に出る区分。 締切後の未着の札は約定待ちか送信中。
const UNARRIVED_STATUS: Partial<
  Record<SpotBidStatusCategory, BiddingExecutionResultUnarrivedStatus>
> = {
  awaitingMatch: BiddingExecutionResultUnarrivedStatus.AWAITING_MATCH,
  sending: BiddingExecutionResultUnarrivedStatus.SENDING,
}

const selectedOf = <T>(
  categories: ReadonlySet<SpotBidStatusCategory>,
  table: Partial<Record<SpotBidStatusCategory, T>>,
): T[] =>
  Array.from(categories).flatMap((c) => {
    const s = table[c]
    return s !== undefined ? [s] : []
  })

/**
 * 期間と約定結果タブのフィルタから RPC の条件を作る。 未選択の軸は「すべて」 (エリアは業務エリアの全部)。
 * ステータスは約定結果の 3 区分が CSV の行に、 約定待ち・送信中が未着の札の件数に効く
 * (件数はフィルタ適用後、 AC-BSP-115)。 どちらにも当たらない区分だけを選んだ時は行も件数も無い。
 * ワークフローは札 (ブロックはグループ) の workflowId で絞り、 「ワークフローなし」 (`NO_WORKFLOW`) は
 * workflowId の無い札を選ぶ (行と未着の件数の両方に効く)。
 */
export const spotExecutionResultRequest = (
  period: DeliveryDatePeriod,
  filter: SpotExecutionResultFilter,
  areaCodes: readonly AreaCode[],
): ListBiddingSpotExecutionResultsRequest =>
  create(ListBiddingSpotExecutionResultsRequestSchema, {
    from: civilDateToProtoDate(period.from),
    to: civilDateToProtoDate(period.to),
    komas: Array.from(filter.komas).sort((a, b) => a - b),
    areaCodes:
      filter.areas.size > 0 ? Array.from(filter.areas) : [...areaCodes],
    sides: Array.from(filter.sides),
    statusSelection:
      filter.statuses.size > 0
        ? create(BiddingExecutionResultStatusSelectionSchema, {
            statuses: selectedOf(filter.statuses, SETTLED_STATUS),
            unarrivedStatuses: selectedOf(filter.statuses, UNARRIVED_STATUS),
          })
        : undefined,
    workflowSelection:
      filter.workflows.size > 0
        ? create(BiddingExecutionResultWorkflowSelectionSchema, {
            workflowIds: Array.from(filter.workflows)
              .filter((w) => w !== NO_WORKFLOW)
              .sort(),
            includeNoWorkflow: filter.workflows.has(NO_WORKFLOW),
          })
        : undefined,
  })

const BID_KIND: Partial<
  Record<BiddingExecutionResultBidKind, ExecutionResultBidKind>
> = {
  [BiddingExecutionResultBidKind.NORMAL]: 'normal',
  [BiddingExecutionResultBidKind.BLOCK]: 'standard',
  [BiddingExecutionResultBidKind.LINK_BLOCK_P]: 'linkP',
  [BiddingExecutionResultBidKind.LINK_BLOCK_C]: 'linkC',
  [BiddingExecutionResultBidKind.LOOP_BLOCK_A]: 'loopA',
  [BiddingExecutionResultBidKind.LOOP_BLOCK_B]: 'loopB',
}

const STATUS: Partial<
  Record<BiddingExecutionResultStatus, ExecutionResultStatus>
> = {
  [BiddingExecutionResultStatus.ALL_MATCHED]: 'allMatched',
  [BiddingExecutionResultStatus.PARTIALLY_MATCHED]: 'partialMatched',
  [BiddingExecutionResultStatus.UNMATCHED]: 'unmatched',
}

/** 応答の 1 行を CSV の 1 行にする。 区分が不明な行 (未設定) は出さない。 */
export const toExecutionResultCsvLines = (
  lines: readonly BiddingExecutionResultLine[],
): ExecutionResultCsvLine[] =>
  lines.flatMap((l) => {
    const status = STATUS[l.status]
    const deliveryDate = protoDateToCivilDate(l.deliveryDate)
    if (deliveryDate === undefined || status === undefined) return []
    return [
      {
        deliveryDate,
        koma: l.koma,
        areaCode: l.areaCode,
        deliveryContractCode: l.deliveryContractCode,
        bidKind: BID_KIND[l.bidKind],
        side: l.side,
        bidPriceYenPerKwh: l.bidPriceYenPerKwh?.value ?? '',
        bidVolumeMw: l.bidVolumeMw?.value ?? '',
        matchedPriceYenPerKwh: l.matchedPriceYenPerKwh?.value,
        matchedVolumeMw: l.matchedVolumeMw?.value ?? '',
        matchedEnergyMwh: l.matchedEnergyMwh?.value ?? '',
        remainingVolumeMw: l.remainingVolumeMw?.value ?? '',
        matchedAmountYen: l.matchedAmountYen?.value,
        status,
        jepxBidNo: l.jepxBidNo,
        createdBy: l.createdBy,
        workflowId: l.workflowId,
        memoJepx: l.memoJepx,
        memoFree: l.memoFree,
      },
    ]
  })
